import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { GlobalSearchController } from '../../services/global-search.controller';
import { GlobalSearchLauncherService } from '../../services/global-search-launcher.service';
import type { GlobalSearchHit } from '../../services/global-search.types';
import { ChromeThemeService } from '../../services/chrome-theme.service';
import { PlatformShortcutService } from '../../services/platform-shortcut.service';
import { SearchShortcutHintComponent } from './search-shortcut-hint.component';

@Component({
  selector: 'app-global-search-launcher',
  templateUrl: './global-search-launcher.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatIconModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ContextMenuModule,
    SearchShortcutHintComponent
  ]
})
export class GlobalSearchLauncherComponent {
  readonly launcher = inject(GlobalSearchLauncherService);
  readonly search = inject(GlobalSearchController);
  readonly shortcuts = inject(PlatformShortcutService);
  readonly themeService = inject(ChromeThemeService);
  private destroyRef = inject(DestroyRef);

  /** Mantiene el DOM montado mientras corre la animación de salida. */
  isVisible = signal(false);
  isClosing = signal(false);
  activeIndex = signal(0);

  private closeTimer?: ReturnType<typeof setTimeout>;
  private static readonly CLOSE_MS = 220;

  queryLength = computed(() => String(this.search.term() ?? '').trim().length);
  queryLabel = computed(() => String(this.search.term() ?? '').trim());
  showSearchShortcutHint = computed(() => this.queryLength() === 0);

  @ViewChild('launcherInput') launcherInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchScopeMenu') searchScopeMenu?: ContextMenu;

  constructor() {
    effect(() => {
      const open = this.launcher.isOpen();
      if (open) {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = undefined;
        }
        this.isClosing.set(false);
        this.isVisible.set(true);
        if (typeof document !== 'undefined') {
          document.body.style.overflow = 'hidden';
        }
        setTimeout(() => this.launcherInput?.nativeElement.focus(), 0);
        return;
      }

      if (this.isVisible() && !this.isClosing()) {
        this.startClosing();
      }
    });

    effect(() => {
      this.search.suggestions();
      this.activeIndex.set(0);
    });

    this.destroyRef.onDestroy(() => {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
      }
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.shortcuts.isSearchLauncherShortcut(event)) {
      event.preventDefault();
      this.launcher.toggle();
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target === this.launcherInput?.nativeElement) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectActive();
    }
  }

  onTermChange(value: string | unknown): void {
    this.search.term.set(value);
    this.search.complete({ query: String(value ?? '') });
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActive(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectActive();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  openSearchScopeMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchScopeMenu?.toggle(event);
  }

  selectItem(item: GlobalSearchHit): void {
    this.search.select(item, () => this.close());
  }

  close(): void {
    if (!this.launcher.isOpen()) {
      return;
    }
    this.launcher.close();
  }

  private startClosing(): void {
    if (this.isClosing()) {
      return;
    }
    this.isClosing.set(true);
    this.closeTimer = setTimeout(() => {
      this.isVisible.set(false);
      this.isClosing.set(false);
      this.activeIndex.set(0);
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    }, GlobalSearchLauncherComponent.CLOSE_MS);
  }

  private moveActive(delta: number): void {
    const items = this.search.suggestions();
    if (items.length === 0) {
      return;
    }
    this.activeIndex.update((current) => {
      const next = current + delta;
      if (next < 0) {
        return items.length - 1;
      }
      if (next >= items.length) {
        return 0;
      }
      return next;
    });
  }

  private selectActive(): void {
    const items = this.search.suggestions();
    const index = this.activeIndex();
    const item = items[index];
    if (item) {
      this.selectItem(item);
    }
  }
}
